import React, { useEffect, useMemo, useState, useRef, useCallback, useContext } from 'react';
import { useWebSocket } from '../context/WebSocketContext';
import GameGrid from '../../XXX/GameSimulator/GameContainer2/GameGrid/GameGrid';
import { GameLog } from '../../XXX/GameSimulator/GameContainer2/Logs/LogsDisplay';
import { ChampionAddData } from '../../XXX/GameSimulator/GameContainer2/GameGrid/AddChampion';
import { GameAssetsProvider } from '../../XXX/context/GameAssetContext';
import SocketContext, { WebSocketAdapter } from './WebSocketAdapter';
import GameAssetsContext from '../../XXX/context/GameAssetContext';

import GameStateContext from '../../XXX/GameSimulator/GameContainer2/GameStateContext';

import './RoomGameContainer.css';
import { GameState } from '../../XXX/gameTypes';

interface RoomGameContainerProps {
  championName?: string; // Optional champion name for combo mode
}

// Inner component that has access to GameAssetsContext
const RoomGameContent: React.FC<RoomGameContainerProps> = ({ championName }) => {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [logs, setLogs] = useState<GameLog[]>([]);
  const [logsByChampion, setLogsByChampion] = useState<Record<string, GameLog[]>>({});
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [selectedChampionId, setSelectedChampionId] = useState<string | null>(null);
  const [isSelectingTarget, setIsSelectingTarget] = useState<boolean>(false);
  const [targetSelectingChampionId, setTargetSelectingChampionId] = useState<string | null>(null);
  
  // Use a ref to store event listeners
  const listenersRef = useRef<Map<string, Array<(data: any) => void>>>(new Map());

  // Routing refs: track which champion owns each combo sequence and each event
  const comboSequenceToChampion = useRef<Map<string, string>>(new Map());
  const eventToChampion = useRef<Map<string, string>>(new Map());

  // Route an incoming event to the correct champion's log bucket
  const routeEventToChampion = useCallback((event: GameLog) => {
    let championId: string | null = null;

    // 1. ComboExecution root events — use the original main champion ID
    if (event.type === 'ComboExecution') {
      championId = event.details?.originalMainChampionId || event.details?.mainChampion || null;
      if (championId && event.sequenceId) {
        comboSequenceToChampion.current.set(event.sequenceId, championId);
      }
    }
    // 2. Events with a known combo sequence ID
    else if (event.sequenceId && comboSequenceToChampion.current.has(event.sequenceId)) {
      championId = comboSequenceToChampion.current.get(event.sequenceId)!;
    }
    // 3. Child events — follow the parent's champion
    else if (event.parentId && eventToChampion.current.has(event.parentId)) {
      championId = eventToChampion.current.get(event.parentId)!;
    }
    // 4. Regular events with championId in details
    else if (event.details?.championId) {
      championId = event.details.championId;
    }

    if (!championId) return;

    // Record this event's owner so future child events can be routed
    eventToChampion.current.set(event.id, championId);

    setLogsByChampion(prev => ({
      ...prev,
      [championId!]: [...(prev[championId!] || []), event]
    }));
  }, []);

  // Get WebSocket context
  const { socket, roomId, status } = useWebSocket();
  
  // Get GameAssets context to preload champion assets
  const { fetchAndSetChampionAssets, championsAssets } = useContext(GameAssetsContext);

  // Function to build a hierarchical structure from flat log list (same as in LogsDisplay)
  const buildLogHierarchy = (logs: GameLog[]) => {
    const logMap: { [id: string]: GameLog } = {};
    const rootLogs: GameLog[] = [];

    // Initialize each log entry in logMap
    logs.forEach(log => {
      log.childEvents = [];
      logMap[log.id] = log;
    });

    // Build the tree
    logs.forEach(log => {
      if (log.parentId && logMap[log.parentId]) {
        logMap[log.parentId].childEvents!.push(log);
      } else {
        rootLogs.push(log);
      }
    });

    return logs; // Return the original logs array, now with childEvents populated
  };



  // Create a socket adapter to make our WebSocket compatible with the old SocketContext API
  const socketAdapter = useMemo(() => {
    if (!socket) return null;
    
    const adapter: WebSocketAdapter = {
      // Helper method to ensure consistent game-message format
      sendGameMessage: (type: string, data: any) => {
        console.log(`[SocketAdapter] Sending game message: ${type}`, data);
        
        socket.send(JSON.stringify({
          type: 'game-message',
          data: {
            type: type,
            data: data
          }
        }));
      },
      
      emit: (event: string, data: any) => {
        console.log(`[SocketAdapter] Emitting event: ${event}`, data);
        
        // Check if this is already a game-message to avoid double nesting
        if (event === 'game-message') {
          // This is already in game-message format, so don't wrap it again
          console.log(`[SocketAdapter] Direct game-message event detected, sending without wrapping`);
          socket.send(JSON.stringify({
            type: 'game-message',
            data
          }));
        }
        // Handle specific grid-related events that need special formatting
        else if (event === 'add-grid-row' || event === 'duplicate-grid-row') {
          console.log(`[SocketAdapter] Handling special grid event: ${event}`);
          adapter.sendGameMessage(event, data);
        } else {
          // Standard event handling
          adapter.sendGameMessage(event, data);
        }
      },
      on: (event: string, callback: (data: any) => void) => {
        // Special case for champion-config events
        if (event === 'champion-config') {
          console.log(`[SocketAdapter] Registering listener for important event: ${event}`, {
            callbackDescription: callback.toString().substring(0, 150),
            currentListenerCount: listenersRef.current.get(event)?.length || 0
          });
        }
        
        // Store the callback in our listeners map
        console.log(`[SocketAdapter] Adding listener for event: ${event}`, {
          listenersMap: {
            hasMap: !!listenersRef.current,
            currentEvents: Array.from(listenersRef.current.keys()),
            listenerCount: listenersRef.current.get(event)?.length || 0
          },
          listenerFunction: callback.toString().substring(0, 100) + '...' // Log part of the function for debugging
        });
        
        if (!listenersRef.current.has(event)) {
          listenersRef.current.set(event, []);
        }
        listenersRef.current.get(event)?.push(callback);
      },
      off: (event: string, callback?: (data: any) => void) => {
        console.log(`[SocketAdapter] Removing listener for event: ${event}`);
        if (callback && listenersRef.current.has(event)) {
          const listeners = listenersRef.current.get(event);
          if (listeners) {
            const index = listeners.indexOf(callback);
            if (index !== -1) {
              listeners.splice(index, 1);
            }
          }
        } else if (!callback) {
          // Remove all listeners for this event
          listenersRef.current.delete(event);
        }
      },
      
      // Method to notify listeners of an event
      notifyListeners: (event: string, data: any) => {
        // Add debug info about the listeners
        const listeners = listenersRef.current.get(event);
        console.log(`[SocketAdapter] Notifying listeners for event: ${event}`, {
          listenersCount: listeners?.length || 0,
          allRegisteredEvents: Array.from(listenersRef.current.keys()),
          data
        });
        
        if (listeners && listeners.length > 0) {
          listeners.forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error(`[SocketAdapter] Error in listener for ${event}:`, error);
            }
          });
        } else {
          console.warn(`[SocketAdapter] No listeners found for event: ${event}`);
        }
      },
      
      // Additional Socket.io compatibility methods
      once: (event: string, callback: (data: any) => void) => {
        console.log(`[SocketAdapter] 'once' method called for event: ${event}`);
        const onceCallback = (data: any) => {
          callback(data);
          adapter.off(event, onceCallback);
        };
        adapter.on(event, onceCallback);
      },
      disconnect: () => {
        console.log(`[SocketAdapter] 'disconnect' method called`);
      },
      removeListener: (event: string) => {
        console.log(`[SocketAdapter] 'removeListener' method called for event: ${event}`);
        listenersRef.current.delete(event);
      },
      removeAllListeners: (event?: string) => {
        console.log(`[SocketAdapter] 'removeAllListeners' method called for event: ${event || 'all'}`);
        if (event) {
          listenersRef.current.delete(event);
        } else {
          listenersRef.current.clear();
        }
      },
      
      // Socket.io-like properties
      connected: status === 'connected',
      disconnected: status !== 'connected',
      id: 'websocket-adapter'
    };
    
    return adapter;
  }, [socket, status]);

  // Initialize the sandbox/game when component mounts
  useEffect(() => {
    if (!socket || !roomId || status !== 'connected') return;
    
    // Check if socket is still valid before using it
    if (socket.readyState !== WebSocket.OPEN) {
      console.warn('[RoomGameContainer] Socket is not open, cannot initialize');
      return;
    }
    
    try {
      // Initialize the game for this room
      if (championName) {
        // Use combo sandbox mode with the provided champion
        console.log(`Initializing combo sandbox for ${championName}`);
        socket.send(JSON.stringify({ 
          type: 'init-combo-sandbox', 
          data: { championName } 
        }));
      } else {
        // Use regular sandbox mode
        socket.send(JSON.stringify({ type: 'init-sandbox', data: {} }));
      }
      
      // Disable automatic names requests for this component
      const originalSend = socket.send;
      socket.send = function(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
        // Check if socket is still valid
        if (this.readyState !== WebSocket.OPEN) {
          console.warn('[RoomGameContainer] Socket is not open, cannot send message');
          return;
        }
        
        // Parse the data to check if it's a get-names request
        if (typeof data === 'string') {
          try {
            const parsedData = JSON.parse(data);
            if (parsedData.type === 'get-names' || parsedData.type === 'get-champions') {
              // Skip sending this request
              console.log('[RoomGameContainer] Prevented get-names request');
              return;
            }
          } catch (e) {
            // If parsing fails, just send the original data
          }
        }
        
        // Otherwise send the message as normal
        return originalSend.call(this, data);
      };
      
      console.log(`RoomGameContainer: Initialized ${championName ? 'combo' : 'regular'} sandbox for room`, roomId);
    } catch (error) {
      console.error('[RoomGameContainer] Error initializing sandbox:', error);
    }
  }, [socket, roomId, status, championName]);

  // Add important debugging console log
  useEffect(() => {
    console.log("[RoomGameContainer] Selected cell state:", selectedCell);
    console.log("[RoomGameContainer] Selected champion ID:", selectedChampionId);
  }, [selectedCell, selectedChampionId]);

  // Preload champion assets when game state changes
  useEffect(() => {
    if (gameState?.champions) {
      gameState.champions.forEach(champion => {
        if (champion.name && !championsAssets[champion.name]) {
          console.log(`[RoomGameContainer] Preloading assets for champion: ${champion.name}`);
          fetchAndSetChampionAssets(champion.name);
        }
      });
    }
  }, [gameState?.champions, championsAssets, fetchAndSetChampionAssets]);

  // Build hierarchical logs — populates childEvents on shared log objects via mutation.
  // Even though we no longer pass this to GameGrid, the side-effect is needed:
  // the same object references live in logsByChampion, so childEvents gets set there too.
  useMemo(() => {
    return buildLogHierarchy([...logs]);
  }, [logs]);

  // Listen for game state updates
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'game-update') {
          //console.log('[RoomGameContainer] Received game state update', data.data);
          // Check if the game state contains champions
          if (data.data.champions && data.data.champions.length > 0) {
            //console.log('[RoomGameContainer] Game state contains champions:', 
            //            data.data.champions.map((c: any) => c.name));
          } else {
            console.log('[RoomGameContainer] Game state contains NO champions');
          }
          setGameState(data.data);
        } else if (data.type === 'game-event') {
          console.log('[RoomGameContainer] Received game event', data.data);
          const event = data.data;
          setLogs(prevLogs => [...prevLogs, event]);
          routeEventToChampion(event);
        } else if (data.type === 'logs-reset') {
          console.log('[RoomGameContainer] Received logs reset request', data.data);
          const resetChampionId = data.data?.championId;
          if (resetChampionId) {
            // Only clear logs for the specific champion
            setLogs(prevLogs => prevLogs.filter(log => eventToChampion.current.get(log.id) !== resetChampionId));
            setLogsByChampion(prev => ({ ...prev, [resetChampionId]: [] }));
          } else {
            // Clear all logs
            setLogs([]);
            setLogsByChampion({});
            comboSequenceToChampion.current.clear();
            eventToChampion.current.clear();
          }
        } else if (data.type === 'champion-config') {
          // Forward champion-config messages to any listeners
          console.log('[RoomGameContainer] Received champion config', {
            championId: data.data?.championId,
            name: data.data?.championName,
            hasRunes: !!data.data?.runes,
            hasItems: !!data.data?.items,
            completeData: data.data
          });
          if (socketAdapter && data.data) {
            // Notify listeners for this specific champion
            socketAdapter.notifyListeners('champion-config', data.data);
          }
        } else if (data.type === 'server-ping') {
          // Handle server ping - no action needed, just acknowledge
          console.log('[RoomGameContainer] Received server ping');
        } else if (data.type === 'pong') {
          // Handle pong response - no action needed
          console.log('[RoomGameContainer] Received pong response');
        } else if (data.type === 'combo-sandbox-ready') {
          // Handle combo sandbox ready confirmation
          console.log('[RoomGameContainer] Combo sandbox ready:', data.data);
        } else if (data.type === 'combo-activated') {
          // Handle combo activation
          console.log('[RoomGameContainer] Combo activated:', data.data);
        } else {
          console.log('[RoomGameContainer] Received unknown message type:', data.type, data);
        }
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    };

    const handleError = (error: Event) => {
      console.error('[RoomGameContainer] WebSocket error:', error);
    };

    const handleClose = (event: CloseEvent) => {
      console.log('[RoomGameContainer] WebSocket closed:', event.code, event.reason);
    };

    // Check if socket is still valid before adding listeners
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.addEventListener('message', handleMessage);
      socket.addEventListener('error', handleError);
      socket.addEventListener('close', handleClose);
    }

    return () => {
      // Check if socket is still valid before removing listeners
      if (socket && socket.readyState !== WebSocket.CLOSED) {
        try {
          socket.removeEventListener('message', handleMessage);
          socket.removeEventListener('error', handleError);
          socket.removeEventListener('close', handleClose);
        } catch (error) {
          console.warn('[RoomGameContainer] Error removing event listeners:', error);
        }
      }
    };
  }, [socket, socketAdapter, routeEventToChampion]);

  const handleCellClick = (row: number, col: number) => {
    const cell = gameState?.grid?.[row]?.[col];
    
    // Always update the selected cell to show the yellow background on the clicked cell
    setSelectedCell({ row, col });
    
    // Check if cell contains a champion and update selectedChampionId accordingly
    if (cell && cell.champion) {
      console.log('Selected champion:', cell.champion);
      setSelectedChampionId(cell.champion.id);
    } else {
      setSelectedChampionId(null);
    }
  };

  const handleSelectTargetClick = (championId: string) => {
    setIsSelectingTarget(true);
    setTargetSelectingChampionId(championId);
    console.log('Selecting target for champion:', championId);
  };

  const handleTargetSelected = (targetChampionId: string) => {
    if (socket && status === 'connected' && targetSelectingChampionId) {
      console.log('Target selected:', targetChampionId, 'for champion:', targetSelectingChampionId);
      
      try {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({
            type: 'game-message',
            data: {
              type: 'change-target',
              data: {
                championId: targetSelectingChampionId,
                targetChampionId: targetChampionId
              }
            }
          }));
        } else {
          console.warn('[RoomGameContainer] Cannot send target selection - socket not open');
        }
      } catch (error) {
        console.error('[RoomGameContainer] Error sending target selection:', error);
      }
      
      // Reset target selection state
      setIsSelectingTarget(false);
      setTargetSelectingChampionId(null);
    }
  };

  // Handle clicking outside to unselect
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      // Only handle if we actually have something selected and the click is outside the game grid
      if ((selectedCell || isSelectingTarget) && e.target) {
        const target = e.target as Element;
        const gameGrid = document.querySelector('.game-grid-container');
        
        // Only cancel selection if clicking outside the game grid area
        if (gameGrid && !gameGrid.contains(target)) {
          handleCancelSelection();
        }
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [selectedCell, isSelectingTarget]);

  // Handle key presses for abilities (Q, W, E, R, A)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only handle if we have a selected champion and the target is not an input/textarea
      if (selectedChampionId && ['Q', 'W', 'E', 'R', 'A'].includes(event.key.toUpperCase()) && socket) {
        const target = event.target as Element;
        
        // Don't interfere with typing in input fields
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || (target as HTMLElement).isContentEditable) {
          return;
        }
        
        // Prevent default behavior for ability keys to avoid conflicts
        event.preventDefault();
        
        const abilityKey = event.key.toUpperCase();
        
        console.log('Using ability:', abilityKey, 'for champion:', selectedChampionId);
        
        try {
          if (socket.readyState === WebSocket.OPEN) {
            if (abilityKey === 'A') {
              socket.send(JSON.stringify({
                type: 'game-message',
                data: {
                  type: 'use-auto-attack',
                  data: {
                    championId: selectedChampionId
                  }
                }
              }));
            } else {
              socket.send(JSON.stringify({
                type: 'game-message',
                data: {
                  type: 'use-ability',
                  data: {
                    championId: selectedChampionId,
                    abilityKey: abilityKey
                  }
                }
              }));
            }
          } else {
            console.warn('[RoomGameContainer] Cannot send ability use - socket not open');
          }
        } catch (error) {
          console.error('[RoomGameContainer] Error sending ability use:', error);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [selectedChampionId, socket]);

  // Handle key presses for items (1-7)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!selectedChampionId || !socket) return;
      
      const key = event.key;
      if (key >= '1' && key <= '7') {
        const target = event.target as Element;
        
        // Don't interfere with typing in input fields
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || (target as HTMLElement).isContentEditable) {
          return;
        }
        
        // Prevent default behavior for item keys to avoid conflicts
        event.preventDefault();
        
        const itemSlot = parseInt(key, 10);
        console.log('Using item in slot:', itemSlot, 'for champion:', selectedChampionId);
        
        try {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({
              type: 'game-message',
              data: {
                type: 'use-item',
                data: {
                  championId: selectedChampionId,
                  itemSlot: itemSlot
                }
              }
            }));
          } else {
            console.warn('[RoomGameContainer] Cannot send item use - socket not open');
          }
        } catch (error) {
          console.error('[RoomGameContainer] Error sending item use:', error);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [selectedChampionId, socket]);

  const handleChampionAddSelected = (championData: ChampionAddData) => {
    if (socket && status === 'connected' && selectedCell) {
      const { row, col } = selectedCell;
      // Assign team based on column: column 0 = blue, column 1 = red
      const team = col === 0 ? 'blue' : 'red';

      const messageData = {
        name: championData.name,
        team,
        level: championData.level || 1,
        position: { x: col, y: row },
      };

      console.log('[RoomGameContainer] Adding champion with data:', messageData);

      const message = JSON.stringify({
        type: 'game-message',
        data: {
          type: 'add-champion',
          data: messageData
        }
      });

      console.log('[RoomGameContainer] Sending message:', message);
      
      try {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(message);
          
          // Also log the raw message for debugging
          try {
            const parsed = JSON.parse(message);
            console.log('[RoomGameContainer] Parsed message structure:', 
                        JSON.stringify(parsed, null, 2));
          } catch (e) { /* ignore parsing errors */ }
        } else {
          console.warn('[RoomGameContainer] Cannot add champion - socket not open');
        }
      } catch (error) {
        console.error('[RoomGameContainer] Error sending champion add:', error);
      }
    } else {
      console.warn('[RoomGameContainer] Cannot add champion - socket status:', status, 'selectedCell:', selectedCell);
    }
  };

  const handleCancelSelection = () => {
    setSelectedCell(null);
    setIsSelectingTarget(false);
    setTargetSelectingChampionId(null);
  };

  // Map champion IDs to teams for the GameStateContext
  const championIdToTeamMap = useMemo(() => {
    if (gameState?.champions) {
      return gameState.champions.reduce((acc, champion) => {
        acc[champion.id] = champion.team;
        return acc;
      }, {} as { [championId: string]: 'blue' | 'red' });
    } else {
      return {};
    }
  }, [gameState]);

  // Implement getChampionById function
  const getChampionById = useCallback((id: string) => {
    if (!gameState?.champions) return null;
    
    // Find champion by ID
    const champion = gameState.champions.find(champ => champ.id === id);
    return champion || null;
  }, [gameState]);



  // If not connected, show loading state
  if (status !== 'connected' || !gameState) {
    return (
      <div className="room-game-container loading">
        <h3>Loading Game Sandbox...</h3>
        <p>Status: {status}</p>
        {championName && <p>Champion: {championName}</p>}
      </div>
    );
  }

  return (
    <SocketContext.Provider value={socketAdapter}>
      <GameStateContext.Provider value={{ gameState, championIdToTeamMap, getChampionById }}>
        <div className='room-game-container'>
    {/*       <div className="game-header">
            <h3>{championName ? `${championName} Combo Sandbox` : 'Champion Sandbox'}</h3>
            <div className="room-info">Room: {roomId}</div>
          </div> */}
          
          <div className="game-content">
            {/* Show OpponentSelector only in combo mode */}
          {/*   {championName && (
              <div className="opponent-selector-container">
                <OpponentSelector />
              </div>
            )} */}
            
            <div className="game-content-main">
              <div className="game-grid-container">
                <GameGrid
                  gameState={gameState}
                  onCellClick={handleCellClick}
                  selectedCell={selectedCell}
                  onChampionSelected={handleChampionAddSelected}
                  onCancelSelection={handleCancelSelection}
                  onSelectTargetClick={handleSelectTargetClick}
                  isSelectingTarget={isSelectingTarget}
                  selectingChampionId={targetSelectingChampionId}
                  onTargetSelected={handleTargetSelected}
                  logsByChampion={logsByChampion}
                />
              </div>
              
             
            </div>
          </div>
        </div>
      </GameStateContext.Provider>
    </SocketContext.Provider>
  );
};

// Main component that wraps the content in GameAssetsProvider
const RoomGameContainer: React.FC<RoomGameContainerProps> = ({ championName }) => {
  return (
    <GameAssetsProvider>
      <RoomGameContent championName={championName} />
    </GameAssetsProvider>
  );
};

export default RoomGameContainer; 